import { useEffect, useState } from "react";
import { Bell, Tag, X } from "lucide-react";
import type { Note } from "@hub/shared";
import { Avatar, Button, Field, IconButton, Input, Modal, Textarea, formatDate, toast } from "@/ui";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import s from "../shared.module.css";

export const NOTE_COLORS = [null, "#2563eb", "#16a34a", "#d97706", "#dc2626", "#7c3aed", "#0891b2"];

/**
 * Plain-text note editor with label, color and reminder date detection. The engine reports a detected date after
 * saving; we offer to turn it into a reminder rather than doing it silently.
 */
export function NoteEditor({ open, onClose, contactId, groupId, note, onSaved }: { open: boolean; onClose: () => void; contactId?: string | null; groupId?: string | null; note?: Note | null; onSaved?: (note: Note) => void }) {
  const [text, setText] = useState(note?.plainText ?? "");
  const [label, setLabel] = useState(note?.label ?? "");
  const [color, setColor] = useState<string | null>(note?.color ?? null);
  const [detected, setDetected] = useState<{ note: Note; at: string } | null>(null);
  // A note belongs to a person (or a group). Opened from Mod+N or the Notes page with nobody chosen, it asks who.
  const needsPerson = !contactId && !groupId && !note;
  const [person, setPerson] = useState<{ contactId: string; name: string; avatarUrl: string | null } | null>(null);
  const [q, setQ] = useState("");
  const search = useEngineQuery("queryContacts", { query: q, limit: 6 }, { enabled: open && needsPerson && !person && q.trim().length > 0 });
  const labels = useEngineQuery("getNoteLabels", undefined);
  const save = useEngineMutation("saveNote");
  const saveReminder = useEngineMutation("saveReminder");

  useEffect(() => {
    if (open) {
      setText(note?.plainText ?? "");
      setLabel(note?.label ?? "");
      setColor(note?.color ?? null);
      setDetected(null);
      setPerson(null);
      setQ("");
    }
  }, [open, note]);

  const submit = async () => {
    if (!text.trim() || (needsPerson && !person)) return;
    const res = await save.mutateAsync({ noteId: note?.id, contactId: contactId ?? person?.contactId ?? note?.contactId ?? null, groupId: groupId ?? note?.groupId ?? null, content: text, plainText: text, label: label.trim() || null, color });
    onSaved?.(res.note);
    if (res.detectedReminderAt && !note) {
      setDetected({ note: res.note, at: res.detectedReminderAt });
      return;
    }
    toast(note ? "Note updated" : "Note saved", "success");
    onClose();
  };

  if (detected) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title="Set a reminder?"
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>No thanks</Button>
            <Button
              variant="primary"
              icon={<Bell size={14} />}
              loading={saveReminder.isPending}
              onClick={async () => {
                await saveReminder.mutateAsync({ contactId: detected.note.contactId, noteId: detected.note.id, title: text.split(/[.\n]/)[0].slice(0, 80), fireAt: detected.at });
                toast("Reminder set", "success");
                onClose();
              }}
            >
              Remind me {formatDate(detected.at, { weekday: "short", month: "short", day: "numeric" })}
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>Your note mentions a date. Hub can remind you on {formatDate(detected.at, { weekday: "long", month: "long", day: "numeric" })}. You can turn this off in Settings.</p>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={note ? "Edit note" : "New note"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={save.isPending} disabled={!text.trim() || (needsPerson && !person)}>
            {note ? "Save" : "Add note"}
          </Button>
        </>
      }
    >
      <div className="u-col" style={{ gap: 12 }}>
        {needsPerson && (
          <Field label="Person">
            {person ? (
              <div className="u-row" style={{ gap: 8 }}>
                <Avatar name={person.name} src={person.avatarUrl} size={22} seed={person.contactId} />
                <span className="u-grow" style={{ fontWeight: 500 }}>{person.name}</span>
                <IconButton label="Choose someone else" onClick={() => setPerson(null)}><X size={14} /></IconButton>
              </div>
            ) : (
              <div className="u-col" style={{ gap: 6 }}>
                <Input autoFocus placeholder="Search a person" value={q} onChange={(e) => setQ(e.target.value)} />
                {(search.data?.items ?? []).map((r) => (
                  <button key={r.contactId} type="button" className={s.chip} data-size="md" style={{ justifyContent: "flex-start" }} onClick={() => setPerson({ contactId: r.contactId, name: r.displayName, avatarUrl: r.avatarUrl })}>
                    <Avatar name={r.displayName} src={r.avatarUrl} size={22} seed={r.contactId} /> {r.displayName} <span className="u-muted u-small">{r.headline}</span>
                  </button>
                ))}
              </div>
            )}
          </Field>
        )}
        <Textarea
          autoFocus={!needsPerson}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What do you want to remember? Dates like next Tuesday or Nov 3 become reminders."
          rows={6}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
          }}
        />
        <div className="u-row" style={{ gap: 12, alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <Field label="Label">
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="fundraising, hiring, ask" list="note-labels" />
              <datalist id="note-labels">{(labels.data ?? []).map((l) => <option key={l} value={l} />)}</datalist>
            </Field>
          </div>
          <div className="u-row" style={{ gap: 6, paddingBottom: 8 }} aria-label="Color">
            <Tag size={14} className="u-muted" />
            {NOTE_COLORS.map((c) => (
              <span key={c ?? "none"} className={s.colorDot} data-active={c === color ? "true" : undefined} style={{ background: c ?? "var(--bg-sunken)" }} onClick={() => setColor(c)} role="button" aria-label={c ?? "No color"} />
            ))}
          </div>
        </div>
        <div className="u-small u-muted">Mod+Enter saves. Notes stay on this device and your Hub workspace; nobody is messaged.</div>
      </div>
    </Modal>
  );
}
